import { useQuery } from '@tanstack/react-query';
import {
  type Lead,
  type LeadEventWithStatusDto,
  LeadQuerySortBy,
  QuerySortDirection,
  Permission,
} from '@up2stay/up2promo-api-client';
import { useCallback, useMemo } from 'react';

import {
  getInitialsFromDisplayName,
  getMemberDisplayName,
  getUserDisplayName,
  getUserInitials,
} from '@/core/components/leads/lead-detail/lead-user-display';
import { collectDistinctLeadTags } from '@/core/components/leads/lead-filter-normalize';
import { usePermissions } from '@/core/hooks/use-permissions';
import { useWorkspaceMembersRoster } from '@/core/hooks/use-workspace-members-roster';
import {
  buildEventNameMap,
  eventNamesFromLead,
} from '@/core/lib/lead-event-names';
import { canMutateLeadRecord } from '@/core/lib/lead-mutate-access';
import { fetchAllLeadEventsPages } from '@/core/lib/lead-events-fetch-all';
import { fetchAllLeadsPages } from '@/core/lib/leads-fetch-all';
import {
  leadEventsQueryKeys,
  leadsQueryKeys,
} from '@/core/query-keys/leads-query-keys';
import { useAuthStore } from '@/core/store/auth.store';

type UseLeadDetailLookupsOptions = {
  workspaceId: string | undefined;
  lead: Lead | undefined;
  eventsEnabled: boolean;
  canUpdateLeads: boolean;
  workspaceLeadsAlwaysShared?: boolean;
  locale: string;
  unknownAuthorLabel: string;
  assigneeLabelOverride: string | null;
};

export function useLeadDetailLookups({
  workspaceId,
  lead,
  eventsEnabled,
  canUpdateLeads,
  workspaceLeadsAlwaysShared = false,
  locale,
  unknownAuthorLabel,
  assigneeLabelOverride,
}: UseLeadDetailLookupsOptions) {
  const user = useAuthStore((state) => state.user);
  const { can } = usePermissions();
  const currentUserDisplayName = getUserDisplayName(user);

  const { data: workspaceMembers = [] } = useWorkspaceMembersRoster(
    workspaceId,
    'lead-timeline',
  );

  const { data: workspaceEvents = [] } = useQuery({
    queryKey: workspaceId
      ? leadEventsQueryKeys.all(workspaceId)
      : leadEventsQueryKeys.root,
    queryFn: async (): Promise<LeadEventWithStatusDto[]> => {
      if (!workspaceId) return [];
      return fetchAllLeadEventsPages(workspaceId);
    },
    enabled: !!workspaceId && eventsEnabled,
  });

  const { data: workspaceLeadsForTags = [] } = useQuery({
    queryKey: workspaceId
      ? leadsQueryKeys.list(workspaceId, 'tags-suggestions')
      : leadsQueryKeys.root,
    queryFn: async (): Promise<Lead[]> => {
      if (!workspaceId) return [];
      return fetchAllLeadsPages(workspaceId, {
        includeArchived: false,
        sortBy: LeadQuerySortBy.ORDER,
        sortOrder: QuerySortDirection.ASC,
      });
    },
    enabled: !!workspaceId,
    staleTime: 30 * 1000,
  });

  const eventNameById = useMemo(
    () => buildEventNameMap(workspaceEvents, eventNamesFromLead(lead)),
    [workspaceEvents, lead],
  );

  const tagSuggestions = useMemo(() => {
    const exclude = workspaceEvents
      .flatMap((ev) => [ev.autoTag, ev.name])
      .map((t) => (t ?? '').trim())
      .filter(Boolean);
    return collectDistinctLeadTags(workspaceLeadsForTags, locale, exclude);
  }, [workspaceLeadsForTags, workspaceEvents, locale]);

  const authorNameByUserId = useMemo(() => {
    const map = new Map<string, { name: string; initials: string }>();

    for (const member of workspaceMembers) {
      if (!member.userId) continue;
      const name = getMemberDisplayName(member);
      if (!name) continue;
      map.set(member.userId, {
        name,
        initials: getInitialsFromDisplayName(name),
      });
    }

    if (user?.id && currentUserDisplayName) {
      map.set(user.id, {
        name: currentUserDisplayName,
        initials: getUserInitials(user),
      });
    }

    return map;
  }, [workspaceMembers, user, currentUserDisplayName]);

  const resolveAuthorDisplayName = useCallback(
    (userId: string | null | undefined) => {
      if (!userId) return unknownAuthorLabel;
      return authorNameByUserId.get(userId)?.name ?? unknownAuthorLabel;
    },
    [authorNameByUserId, unknownAuthorLabel],
  );

  const resolveAuthorInitials = useCallback(
    (userId: string | null | undefined) => {
      if (!userId) return '?';
      return authorNameByUserId.get(userId)?.initials ?? '?';
    },
    [authorNameByUserId],
  );

  const assigneeDisplayReadonly = useMemo(() => {
    if (assigneeLabelOverride) return assigneeLabelOverride;
    if (!lead) return '';
    const aid = lead.assigneeUserId?.trim() ?? '';
    if (!aid) return ''; // unassigned — callers show Niemand via picker labels
    return resolveAuthorDisplayName(aid);
  }, [assigneeLabelOverride, lead, resolveAuthorDisplayName]);

  // Own / assigned / shared → LEADS_UPDATE. Others’ private also needs
  // LEADS_UPDATE_OTHERS (read-only detail when only READ_OTHERS).
  const canManageCrm = canMutateLeadRecord({
    lead,
    viewerUserId: user?.id,
    canUpdate: canUpdateLeads,
    canUpdateOthers: can(Permission.LEADS_UPDATE_OTHERS),
    workspaceLeadsAlwaysShared,
  });
  const canEditEvents = Boolean(eventsEnabled && canManageCrm);
  const canCreateEvents = Boolean(
    canEditEvents && can(Permission.EVENTS_MANAGE),
  );
  const canEditTags = canManageCrm;

  return {
    workspaceEvents,
    tagSuggestions,
    eventNameById,
    resolveAuthorDisplayName,
    resolveAuthorInitials,
    assigneeDisplayReadonly,
    canManageCrm,
    canEditEvents,
    canCreateEvents,
    canEditTags,
  };
}
